import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import type { Intervention, IkList, IkOne } from "../lib/interventions";
import { DOMAENEN, HAERTE, PHASEN } from "../lib/interventions";
import "../styles.interventionen.css";

/**
 * /app/interventionen — durchsuchbarer, filterbarer Interventionskatalog.
 * Detailansicht mit ALLEN Feldern; "Nicht einsetzen" und rechtlicher Vorbehalt
 * hervorgehoben. Inhalt kommt verbatim aus Gold (kpi-orchestrator /v1/interventions).
 * Deep-Link: /app/interventionen?code=IV-XYZ
 */
export default function InterventionenPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [domaene, setDomaene] = useState("");
  const [phase, setPhase] = useState("");
  const [haerte, setHaerte] = useState("");
  const selected = params.get("code") || "";

  const list = useQuery({
    queryKey: ["ik-list", effectiveTenantId, domaene, phase, haerte],
    queryFn: () => api.get<IkList>(
      `/interventions?limit=500${domaene ? `&domaene=${domaene}` : ""}${phase ? `&phase=${phase}` : ""}${haerte ? `&haerte=${haerte}` : ""}`),
  });
  const detail = useQuery({
    enabled: !!selected,
    queryKey: ["ik-detail", effectiveTenantId, selected],
    queryFn: () => api.get<IkOne>(`/interventions/${encodeURIComponent(selected)}`),
  });

  const items = useMemo(() => {
    const all = list.data?.items ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((i) =>
      (i.name || "").toLowerCase().includes(needle) ||
      (i.kurzbeschreibung || "").toLowerCase().includes(needle) ||
      (i.wann_sinnvoll || "").toLowerCase().includes(needle) ||
      (i.code || "").toLowerCase().includes(needle));
  }, [list.data, q]);

  const open = (code: string) => { const p = new URLSearchParams(params); p.set("code", code); setParams(p); };
  const close = () => { const p = new URLSearchParams(params); p.delete("code"); setParams(p); };

  return (
    <div className="sk-page ik-page">
      <header className="sk-hero">
        <div>
          <h1>Interventionskatalog</h1>
          <p className="hero-sub">
            Die richtige Intervention zur Situation — als exakter Datensatz. {list.data?.count ?? "…"} Interventionen,
            Stand {list.data?._meta?.katalog_version ?? ""}. Inhalt verbatim aus der Quelle der Wahrheit.
          </p>
        </div>
      </header>

      <div className="sk-block ik-toolbar" role="search">
        <input className="ik-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Suchen: Name, Situation, Kurzbeschreibung, Code…" aria-label="Interventionen durchsuchen" />
        <label className="ik-filter">Domäne
          <select value={domaene} onChange={(e) => setDomaene(e.target.value)} aria-label="Domäne filtern">
            <option value="">alle</option>
            {Object.entries(DOMAENEN).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="ik-filter">Phase
          <select value={phase} onChange={(e) => setPhase(e.target.value)} aria-label="Phase filtern">
            <option value="">alle</option>{PHASEN.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="ik-filter">Härte
          <select value={haerte} onChange={(e) => setHaerte(e.target.value)} aria-label="Härte filtern">
            <option value="">alle</option>{HAERTE.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </label>
      </div>

      <div className="ik-layout">
        <ul className="ik-list" aria-label="Interventionsliste">
          {list.isLoading && <li className="ik-muted">lädt…</li>}
          {items.map((i) => (
            <li key={i.code}>
              <button className={`ik-card ${selected === i.code ? "active" : ""}`} onClick={() => open(i.code)}>
                <span className="ik-card-name">{i.name}</span>
                <span className="ik-card-meta">
                  <span className="ik-tag">{i.kerndomaene_name || i.kategorie || i.tenant}</span>
                  {i.haerte && <span className={`ik-tag h-${i.haerte}`}>{i.haerte}</span>}
                  {i.hat_rechtlichen_vorbehalt && <span className="ik-tag warn" title="rechtlicher Vorbehalt">§</span>}
                </span>
              </button>
            </li>
          ))}
          {!list.isLoading && items.length === 0 && <li className="ik-muted">keine Treffer</li>}
        </ul>

        {selected && (
          <aside className="sk-block ik-detail" aria-live="polite">
            <button className="ik-close" onClick={close} aria-label="Detail schließen">×</button>
            {detail.isLoading && <p className="ik-muted">lädt…</p>}
            {detail.data && <InterventionDetail iv={detail.data.intervention} version={detail.data._meta?.katalog_version} />}
          </aside>
        )}
      </div>
    </div>
  );
}

export function InterventionDetail({ iv, version }: { iv: Intervention; version?: string }) {
  const Field = ({ label, value }: { label: string; value?: string | null }) =>
    value ? (<div className="ik-field"><div className="ik-field-l">{label}</div><div className="ik-field-v">{value}</div></div>) : null;
  return (
    <div className="ik-detail-body">
      <div className="ik-detail-head">
        <h2>{iv.name}</h2>
        <code className="ik-code">{iv.code}</code>
      </div>
      <div className="ik-badges">
        {iv.kerndomaene_name && <span className="ik-tag">{iv.kerndomaene_name}</span>}
        {iv.phase && <span className="ik-tag">Phase: {iv.phase}</span>}
        {iv.ebene && <span className="ik-tag">Ebene: {iv.ebene}</span>}
        {iv.haerte && <span className={`ik-tag h-${iv.haerte}`}>Härte: {iv.haerte}</span>}
        {iv.personenabhaengigkeit && <span className="ik-tag">Personenabh.: {iv.personenabhaengigkeit}</span>}
        {iv.kategorie && <span className="ik-tag">{iv.kategorie}</span>}
        {iv.is_1und1_spezifisch && <span className="ik-tag warn">1&1-spezifisch</span>}
      </div>

      <Field label="Kurzbeschreibung" value={iv.kurzbeschreibung} />
      <Field label="Nutzen (Kundensicht)" value={iv.nutzen_kundensicht} />
      <Field label="Wann sinnvoll (Situation)" value={iv.wann_sinnvoll} />
      <Field label="Ziel" value={iv.ziel} />
      <Field label="Wirkung" value={iv.wirkung} />
      <Field label="Wirkprinzip" value={iv.wirkprinzip} />
      <Field label="Zielwirkung (1&1)" value={iv.zielwirkung} />

      {iv.nicht_einsetzen && (
        <div className="ik-warn ik-warn-stop">
          <div className="ik-warn-l">⛔ Nicht einsetzen</div>
          <div className="ik-warn-v">{iv.nicht_einsetzen}</div>
        </div>
      )}
      {(iv.rechtlicher_vorbehalt || iv.rechtlicher_vorbehalt_detail) && (
        <div className="ik-warn ik-warn-legal">
          <div className="ik-warn-l">§ Rechtlicher Vorbehalt</div>
          <div className="ik-warn-v">{iv.rechtlicher_vorbehalt || iv.rechtlicher_vorbehalt_detail}</div>
        </div>
      )}
      <p className="ik-source">Exakt aus dem Interventionskatalog {version ? `(${version})` : ""} · Mandant {iv.tenant} · KI sucht/routet nur.</p>
    </div>
  );
}
